import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, LockKeyhole } from "lucide-react";
import { staffApi, setSession, getToken, fmtErr } from "../api";
import BrandLogo from "../../components/BrandLogo";

const Login = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (getToken()) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await staffApi.post("/auth/login", { email: form.email.trim(), password: form.password });
      setSession(data.access_token, data.user);
      navigate("/app");
    } catch (err) {
      setError(err.response?.status === 401 ? "Invalid email or password." : fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1128] flex items-center justify-center px-4" data-testid="staff-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3">
            <BrandLogo compact />
            <span className="leading-none text-left">
              <span className="block font-display font-extrabold tracking-tight text-white">GR CONNECT</span>
              <span className="block font-mono-tech text-[9px] tracking-[0.3em] text-slate-400 uppercase">HRMS · ERP Portal — G. R. Engineering</span>
            </span>
          </div>
        </div>
        <div className="bg-white p-8 rounded-sm shadow-2xl" data-testid="staff-login-form">
          <h1 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
            <LockKeyhole size={16} className="text-[#C5221F]" /> Employee Sign In
          </h1>
          <p className="mt-1.5 text-xs text-slate-500">Same credentials as the GR Attendance mobile app.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="sl-email" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Email</label>
              <input
                id="sl-email" type="email" required autoComplete="email" data-testid="staff-email-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F] focus:ring-1 focus:ring-[#C5221F]"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="sl-pass" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Password</label>
              <input
                id="sl-pass" type="password" required autoComplete="current-password" data-testid="staff-password-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F] focus:ring-1 focus:ring-[#C5221F]"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            {error && (
              <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="staff-login-error">{error}</p>
            )}
            <button
              type="submit" disabled={busy} data-testid="staff-login-submit"
              className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors duration-150 inline-flex items-center justify-center gap-2"
            >
              {busy && <Loader2 size={14} className="animate-spin" />} Sign In
            </button>
          </form>
          <p className="mt-5 text-[11px] text-slate-400 text-center">
            Attendance check-in / check-out is available in the mobile app only.
          </p>
        </div>
        <p className="mt-6 text-center">
          <a href="/" className="text-[11px] font-bold uppercase tracking-widest text-slate-400 hover:text-white transition-colors" data-testid="staff-back-to-site">
            Back to grengg.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
